import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2 } from "lucide-react";
import { streamPost } from "@/lib/api";
import { useLang } from "@/i18n";

const AIChat = () => {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const listRef = useRef(null);

  const sessionId = useMemo(() => {
    let sid = localStorage.getItem("fitnastic_chat_session");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("fitnastic_chat_session", sid);
    }
    return sid;
  }, []);

  const scrollDown = () => {
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }), 50);
  };

  const send = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    setThinking(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    scrollDown();
    streamPost("/ai/chat", { message: text, session_id: sessionId, lang }, {
      onDelta: (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
          return copy;
        });
        scrollDown();
      },
      onDone: () => setThinking(false),
      onError: () => {
        setThinking(false);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: t.error_generic };
          return copy;
        });
      },
    });
  };

  const visibleMessages = messages.length === 0 ? [{ role: "assistant", text: t.ai_chat_greeting }] : messages;

  return (
    <>
      <button
        data-testid="ai-chat-open-button"
        onClick={() => setOpen(!open)}
        aria-label="Open AI chat"
        className="fixed bottom-24 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-volt text-ink shadow-lg shadow-black/40 transition-transform duration-200 hover:scale-110"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-7 w-7" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-40 right-4 z-40 flex h-[28rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-panel shadow-2xl shadow-black/60 sm:right-6"
            data-testid="ai-chat-panel"
          >
            <div className="flex items-center gap-2.5 border-b border-zinc-800 bg-surface px-4 py-3.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-volt">
                <Bot className="h-4.5 w-4.5 text-ink" />
              </span>
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-widest text-white" data-testid="ai-chat-title">{t.ai_chat_title}</p>
                <p className="flex items-center gap-1 text-[10px] text-zinc-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Online
                </p>
              </div>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="ai-chat-messages">
              {visibleMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-volt text-ink" : "border border-zinc-800 bg-ink text-zinc-200"
                    }`}
                  >
                    {m.text || (thinking && i === visibleMessages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-800 bg-surface p-3" data-testid="ai-chat-form">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.ai_chat_placeholder}
                maxLength={500}
                className="flex-1 rounded-full border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-volt"
              />
              <button
                data-testid="ai-chat-send-button"
                type="submit"
                disabled={thinking || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-volt text-ink transition-transform hover:scale-105 disabled:opacity-50"
                aria-label="Send"
              >
                {thinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChat;
